import {HttpHeaders} from '@angular/common/http';
import {USER_TYPES} from '../../AppConstants/UserConstants';
import {AppStateService} from '../../services/appstate/app.state.service';

/**
 * IAM hints for the mergeable Nest API (`backend/smart-dining-admin-api`, `resolve-principal.ts`).
 * Used by {@link HrAdminService} and {@link AuthInterceptor} so **employees/** and **hr/** routes
 * see the same principal (fixes INTERNAL reads needing `x-sd-manage-internal`, org admins needing `org_admin` + company UID).
 *
 * Production gateways normally derive principal from JWT — these headers are for non-production / explicit devHeaders only.
 */
export function mergeSmartDiningPrincipalHeaders(
  headers: HttpHeaders,
  appState: AppStateService,
  hrAdminDevHeaders: Record<string, string> | undefined,
  attachSessionHints: boolean,
): HttpHeaders {
  let h = headers;
  if (hrAdminDevHeaders) {
    for (const [k, v] of Object.entries(hrAdminDevHeaders)) {
      if (v != null && v !== '' && !h.has(k)) {
        h = h.set(k, String(v));
      }
    }
  }
  if (!attachSessionHints) {
    return h;
  }
  const uid = appState.user.userId || appState.user.userDetails?.user;
  if (uid && !h.has('x-sd-user-id')) {
    h = h.set('x-sd-user-id', uid);
  }
  const company = appState.user.companyUID?.trim();
  if (company && !h.has('x-sd-company-uid')) {
    h = h.set('x-sd-company-uid', company);
  }
  const ridRaw = appState.restaurant.restaurantId || appState.restaurant.id;
  const rid = typeof ridRaw === 'string' ? ridRaw.trim() : '';
  if (rid && !h.has('x-sd-restaurant-id')) {
    h = h.set('x-sd-restaurant-id', rid);
  }
  const ut = appState.user.userDetails?.userType;
  if (ut === USER_TYPES.SUPER_USER) {
    if (!h.has('x-sd-role')) {
      h = h.set('x-sd-role', 'super_admin');
    }
    if (!h.has('x-sd-view-all')) {
      h = h.set('x-sd-view-all', 'true');
    }
    if (!h.has('x-sd-manage-internal')) {
      h = h.set('x-sd-manage-internal', 'true');
    }
  } else if (ut === USER_TYPES.ACCOUNT_MANAGER) {
    if (!h.has('x-sd-role')) {
      if (company) {
        h = h.set('x-sd-role', 'org_admin');
      } else if (rid) {
        h = h.set('x-sd-role', 'restaurant_admin');
      } else {
        h = h.set('x-sd-role', 'super_admin');
      }
    }
  }
  return h;
}
